import React from 'react';
import { motion } from 'framer-motion';
import { HardHat, Building2, Home } from 'lucide-react';

const Expertise = () => {
  const expertiseData = [
    {
      id: 'builders',
      title: 'Builder Projects',
      icon: <HardHat size={48} strokeWidth={1.5} className="text-yellow-400" />,
      backgroundImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      items: [
        'Amar Builders',
        'Premium construction services',
        'Residential & commercial builds',
        'Quality assurance'
      ]
    },
    {
      id: 'corporate',
      title: 'Corporate Spaces',
      icon: <Building2 size={48} strokeWidth={1.5} className="text-yellow-400" />,
      backgroundImage: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      items: [
        'Wigfield Company offices',
        'AP 4 Kharadi lobbies and cafeteria',
        'Custom corporate solutions',
        'Brand-aligned designs'
      ]
    },
    {
      id: 'bungalows',
      title: 'Bungalow Interiors',
      icon: <Home size={48} strokeWidth={1.5} className="text-yellow-400" />,
      backgroundImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      items: [
        'Dr. Narayan residence',
        'Dr. Patil home',
        'Nilons project',
        'Luxury bespoke designs'
      ]
    }
  ];

  // Animation variants - Optimized for performance
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  return (
    <section className="py-20 relative overflow-hidden bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
      {/* Architecture-themed background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900"></div>

        {/* Subtle geometric patterns */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] opacity-10"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Our <span className="text-yellow-400">Services</span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto mb-8 rounded-full"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Our dedicated teams excel in transforming spaces across diverse sectors with precision craftsmanship and innovative design solutions.
          </p>
        </motion.div>

        {/* Expertise Cards */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-10"
        >
          {expertiseData.map((expertise, index) => (
            <motion.div
              key={expertise.id}
              variants={item}
              whileHover={{ y: -15, scale: 1.05 }}
              className="group relative overflow-hidden rounded-3xl border border-yellow-500/30 bg-black/20 backdrop-blur-xl transition-all duration-700 hover:shadow-2xl shadow-xl hover:shadow-yellow-500/30 hover:border-yellow-400/50"
              style={{
                border: '1px solid rgba(255, 215, 0, 0.3)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              {/* Card Background Image */}
              <div className="absolute inset-0">
                <img
                  src={expertise.backgroundImage}
                  alt={expertise.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70 backdrop-blur-sm"></div>
              </div>

              {/* Gradient glow effect on hover */}
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400/20 via-transparent to-yellow-400/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              {/* Content */}
              <div className="relative p-6 sm:p-8 h-full flex flex-col z-10">
                {/* Icon and Title */}
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-yellow-500/30 to-yellow-600/30 backdrop-blur-lg border border-yellow-500/50 shadow-xl mb-4 flex items-center justify-center transform transition-transform duration-500 group-hover:scale-110">
                    {expertise.icon}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white drop-shadow-lg tracking-wide">
                    {expertise.title}
                  </h3>
                </div>

                {/* List Items */}
                <ul className="space-y-3 sm:space-y-4 flex-grow mb-6">
                  {expertise.items.map((item, itemIndex) => (
                    <motion.li
                      key={itemIndex}
                      initial={{ opacity: 0, x: -25 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3 }}
                      className="flex items-start gap-3 group"
                    >
                      <div className="mt-2 w-3 h-3 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 flex-shrink-0 shadow-lg flex items-center justify-center">
                        <div className="w-1 h-1 bg-white rounded-full"></div>
                      </div>
                      <span className="text-white text-sm sm:text-base leading-relaxed group-hover:text-yellow-100 transition-colors font-medium drop-shadow-sm flex-1 text-left">
                        {item}
                      </span>
                    </motion.li>
                  ))}
                </ul>

                {/* Bottom Accent */}
                <div className="pt-4 border-t border-yellow-500/50">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-[10px] sm:text-xs font-semibold uppercase tracking-wider">Professional Expertise</span>
                    <div className="flex gap-0.5 sm:gap-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-current text-yellow-400" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Glassy Hover Effects */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br from-yellow-400/10 to-transparent pointer-events-none"></div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Expertise;